import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "./hooks";
import { updateRole } from "./slices/authSlice";
import OnboardingWizard from "./components/OnboardingWizard";

const getRoleFromPath = (pathname: string): string | null => {
  const lower = pathname.toLowerCase();
  if (lower.startsWith("/admin")) return "Admin";
  if (lower.startsWith("/manager")) return "Manager";
  if (lower.startsWith("/cashier")) return "Cashier";
  return null;
};

export const AuthGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const activeRole = useAppSelector((state) => state.auth.activeRole);
  const userRoles = useAppSelector((state) => state.auth.user?.roles);
  const mainRole = useAppSelector((state) => state.auth.user?.main_role);
  const searchParams = new URLSearchParams(location.search);
  const isAuthenticatedParam = searchParams.get("isAuthenticated") === "true";

  // Track previous values to distinguish navigation vs profile switch
  const prevPathRef = useRef(location.pathname);
  const prevRoleRef = useRef(activeRole);

  console.log("Auth gate redirect. Role: ", activeRole);
  console.log("Main role: ", mainRole);
  console.log("Other roles: ", userRoles);

  useEffect(() => {
    if (activeRole) {
      const currentPath = location.pathname.toLowerCase();
      const pathChanged = prevPathRef.current !== location.pathname;
      // const roleChanged = prevRoleRef.current !== activeRole;

      // Update refs for next render
      prevPathRef.current = location.pathname;
      prevRoleRef.current = activeRole;

      // Don't redirect if isAuthenticated=true is in the query params (user wants to see landing page)
      if (isAuthenticatedParam && currentPath === "/") {
        return;
      }

      // Redirect if we're on the root path
      if (currentPath === "/") {
        navigate(`/${activeRole.toLowerCase()}`, { replace: true });
        return;
      }

      // Check if there's a mismatch between current path and active role
      const pathRole = getRoleFromPath(location.pathname);
      const hasMismatch = pathRole && pathRole.toLowerCase() !== activeRole.toLowerCase();

      if (hasMismatch) {
        // Check if user has the role required by the path
        const hasPathRole = userRoles?.includes(pathRole) || mainRole === pathRole || mainRole === "Admin";

        if (pathChanged && hasPathRole) {
          // User navigated to a different role's page and has that role
          // Switch their active role to match the page, stay on the page
          dispatch(updateRole({ value: pathRole }));
        } else {
          // Either user switched profiles (roleChanged) or doesn't have the required role
          // Redirect to the active role's dashboard
          navigate(`/${activeRole.toLowerCase()}`, { replace: true });
        }
      }
    }
  }, [activeRole, navigate, location.pathname, isAuthenticatedParam, userRoles, mainRole, dispatch]);

  return (
    <>
      <Outlet />
      <OnboardingWizard />
    </>
  );
};
